/** External Shopify storefront — opens in the same tab. */
export const SHOP_URL = "https://shopalltimepaintandplaster.com/";
/** Custom 12×12 sample board product in the Shopify store. */
export const SAMPLE_BOARD_URL = "https://shopalltimepaintandplaster.com/products/custom-sample-board-preparation-12-12";

export interface NavItem {
  name: string;
  href: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  viewAll?: NavItem;
}

/** Plaster & specialty finish pages (lead offering — always listed first). */
export const plasterNav: NavGroup = {
  title: "Plaster & Finishes",
  items: [
    { name: "Plaster & Limewash Hub", href: "/plaster/" },
    { name: "Venetian & Decorative Plaster", href: "/services/venetian-plaster/" },
    { name: "Limewash", href: "/services/limewash-painting/" },
    { name: "Microcement & Plaster Showers", href: "/plaster-showers-nj/" },
    { name: "Fireplace Finishes", href: "/2-day-fireplace-transformation/" },
    { name: "Kitchen Hoods & Architectural Finishes", href: "/services/decorative-kitchen-finishes/" },
    { name: "Wallpaper Installation", href: "/services/wallpaper/" },
    { name: "Wall Printing & Murals", href: "/services/wall-printing/" },
  ],
  viewAll: { name: "View all plaster & decorative finishes →", href: "/plaster-services/" },
};

/** Painting services. */
export const paintingNav: NavGroup = {
  title: "Painting",
  items: [
    { name: "Interior Painting", href: "/services/interior-painting/" },
    { name: "Color Drenching", href: "/color-drenching/" },
    { name: "New Construction Painting", href: "/services/new-construction-painting/" },
    { name: "Commercial Painting", href: "/services/commercial-painting/" },
    { name: "Exterior Painting", href: "/services/exterior-painting/" },
    { name: "All Painting Services", href: "/painting-services/" },
  ],
};

/** Lower-priority services — kept live, linked from the painting hub and footer only. */
export const additionalNav: NavGroup = {
  title: "Additional Services",
  items: [
    { name: "Cabinet Painting & Refinishing", href: "/services/cabinet-refinishing/" },
    { name: "Wood Staining & Refinishing", href: "/services/wood-staining-refinishing/" },
    { name: "Deck Staining", href: "/services/deck-staining/" },
    { name: "Wallpaper Installation", href: "/services/wallpaper/" },
  ],
};

/** Simplified commercial menu — deeper vertical pages stay live via the hub, footer and internal links. */
export const commercialNav: NavGroup = {
  title: "Commercial",
  items: [
    { name: "Commercial Painting", href: "/services/commercial-painting/" },
    { name: "Commercial Decorative Finishes", href: "/services/specialty-finishes/" },
    { name: "Retail & Hospitality", href: "/retail-store-painting/" },
    { name: "Offices & Commercial Interiors", href: "/office-painting/" },
  ],
  viewAll: { name: "View all commercial services →", href: "/services/commercial-painting/" },
};

/** Full commercial list kept for the footer / site directory. */
export const commercialFooterNav: NavGroup = {
  title: "Commercial Painting",
  items: [
    { name: "Commercial Interior Painting", href: "/commercial-interior-painting/" },
    { name: "Commercial Exterior Painting", href: "/commercial-exterior-painting/" },
    { name: "Office Painting", href: "/office-painting/" },
    { name: "Property Management & Multifamily", href: "/property-management-painting/" },
    { name: "Medical & Healthcare Facilities", href: "/medical-facility-painting/" },
    { name: "Schools & Daycare", href: "/daycare-school-painting/" },
    { name: "Retail Stores", href: "/retail-store-painting/" },
    { name: "Restaurants & Hospitality", href: "/restaurant-hospitality-painting/" },
    { name: "Warehouse & Industrial", href: "/warehouse-industrial-painting/" },
    { name: "Fitness Centers", href: "/fitness-center-painting/" },
    { name: "Industrial Coatings", href: "/services/industrial-painting/" },
    { name: "Parking Lot Striping", href: "/services/parking-lot-striping/" },
    { name: "Commercial Painting Overview", href: "/services/commercial-painting/" },
  ],
};


/** Town / county landing pages. */
export const areasNav: NavGroup = {
  title: "Service Areas",
  items: [
    { name: "All Service Areas", href: "/service-areas/" },
    { name: "Wayne, NJ", href: "/painters-wayne-nj/" },
    { name: "Plaster & Limewash — Wayne", href: "/plaster-limewash-wayne-nj/" },
    { name: "Ridgewood, NJ", href: "/painters-ridgewood-nj/" },
    { name: "Franklin Lakes, NJ", href: "/painters-franklin-lakes-nj/" },
    { name: "Mahwah, NJ", href: "/painters-mahwah-nj/" },
    { name: "Ramsey, NJ", href: "/painters-ramsey-nj/" },
    { name: "Montclair, NJ", href: "/painters-montclair-nj/" },
    { name: "Paramus, NJ", href: "/painters-paramus-nj/" },
    { name: "Hoboken, NJ", href: "/painters-hoboken-nj/" },
  ],
};

/** Tools, content and company pages. */
export const resourcesNav: NavGroup = {
  title: "Resources",
  items: [
    { name: "Paint Calculator", href: "/paint-calculator/" },
    { name: "Paint Visualizer", href: "/visualizer/" },
    { name: "Projects", href: "/projects/" },
    { name: "Gallery", href: "/gallery/" },
    { name: "Blog", href: "/blog/" },
    
    { name: "FAQ", href: "/faq/" },
    { name: "Press & Media", href: "/press/" },
    { name: "Book an Estimate", href: "/book/" },
  ],
};

export const companyNav: NavGroup = {
  title: "Company",
  items: [
    { name: "About Us", href: "/about/" },
    { name: "All Services", href: "/services/" },
    { name: "Service Areas", href: "/service-areas/" },
    { name: "Press & Media", href: "/press/" },
    { name: "Contact", href: "/contact/" },
    { name: "Privacy Policy", href: "/privacy-policy/" },
    { name: "Terms of Service", href: "/terms-of-service/" },
  ],
};

/** Portfolio / work pages. */
export const workNav: NavGroup = {
  title: "Projects",
  items: [
    { name: "Plaster & Specialty Finishes", href: "/gallery/specialty-finishes/" },
    { name: "Painting Projects", href: "/projects/?category=Interior%20Painting" },
  ],
};

/** Resources + primary conversion link folded into the Services mega menu. */
export const moreNav: NavGroup = {
  title: "Resources & Get Started",
  items: [
    { name: "Service Areas", href: "/service-areas/" },
    { name: "FAQ", href: "/faq/" },
    { name: "Blog", href: "/blog/" },
    { name: "Schedule a Finish Consultation", href: "/book/?type=limewash-color" },
    { name: "Contact Us", href: "/contact/" },
  ],
  viewAll: { name: "Request a free estimate →", href: "/contact/?source=menu" },
};

export const megaMenuGroups: NavGroup[] = [plasterNav, paintingNav, commercialNav];
export const footerGroups: NavGroup[] = [
  plasterNav,
  paintingNav,
  additionalNav,
  commercialFooterNav,
  areasNav,
  resourcesNav,
  companyNav,
];

